import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { saveBlockbrainCredentials } from "@/services/blockbrain/saveBlockbrainCredentials";
import { getBlockbrainCredentials } from "@/services/blockbrain/getBlockbrainCredentials";
import { testBlockbrainCredentials, type BlockbrainTestResult } from "@/services/blockbrain/testBlockbrainCredentials";
import { listBlockbrainBots, type BlockbrainBot } from "@/services/blockbrain/listBlockbrainBots";
import BlockbrainStore from "@/stores/BlockbrainStore";

export function useBlockbrainCredentials() {
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<BlockbrainTestResult | null>(null);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [bots, setBots] = useState<BlockbrainBot[]>([]);
  const [isLoadingBots, setIsLoadingBots] = useState<boolean>(false);
  const [botsError, setBotsError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const localApiKey = BlockbrainStore(state => state.localApiKey);
  const localApiUrl = BlockbrainStore(state => state.localApiUrl);
  const localBotId = BlockbrainStore(state => state.localBotId);
  const hasChanges = BlockbrainStore(state => state.hasChanges);
  const setLocalApiKey = BlockbrainStore(state => state.setLocalApiKey);
  const setLocalApiUrl = BlockbrainStore(state => state.setLocalApiUrl);
  const setLocalBotId = BlockbrainStore(state => state.setLocalBotId);
  const setOriginalApiKey = BlockbrainStore(state => state.setOriginalApiKey);
  const setOriginalApiUrl = BlockbrainStore(state => state.setOriginalApiUrl);
  const setOriginalBotId = BlockbrainStore(state => state.setOriginalBotId);

  const { data: credentials, isLoading } = useQuery({
    queryKey: ["blockbrainCredentials"],
    queryFn: getBlockbrainCredentials,
  });

  useEffect(() => {
    if (credentials) {
      setOriginalApiKey(credentials.apiKey);
      setOriginalApiUrl(credentials.apiUrl);
      setOriginalBotId(credentials.botId);
    }
  }, [credentials, setOriginalApiKey, setOriginalApiUrl, setOriginalBotId]);

  const { mutate: saveCredentialsMutation, isPending: isSaving } = useMutation({
    mutationFn: () => saveBlockbrainCredentials(localApiKey, localApiUrl, localBotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blockbrainCredentials"] });
      setOriginalApiKey(localApiKey);
      setOriginalApiUrl(localApiUrl);
      setOriginalBotId(localBotId);
    },
  });

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiKey(e.target.value.trim());
  };

  const handleApiUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiUrl(e.target.value.trim());
  };

  const handleBotIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalBotId(e.target.value.trim());
  };

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const handleSaveCredentials = () => {
    setLocalApiKey(localApiKey.trim());
    setLocalApiUrl(localApiUrl.trim());
    setLocalBotId(localBotId.trim());
    saveCredentialsMutation();
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testBlockbrainCredentials({
        apiKey: localApiKey.trim(),
        apiUrl: localApiUrl.trim(),
        botId: localBotId.trim(),
      });
      setTestResult(result);
    } finally {
      setIsTesting(false);
    }
  };

  const handleLoadBots = async () => {
    console.log("[Blockbrain] Loading bots…", { apiUrl: localApiUrl });
    setIsLoadingBots(true);
    setBotsError(null);
    try {
      const result = await listBlockbrainBots({
        apiKey: localApiKey.trim(),
        apiUrl: localApiUrl.trim(),
      });
      console.log("[Blockbrain] /api/blockbrain-bots response:", result);
      if (result.ok && result.bots) {
        setBots(result.bots);
        if (result.bots.length === 0) {
          setBotsError("Keine Bots fuer diesen API-Key gefunden.");
        } else if (result.bots.length === 1 && !localBotId) {
          setLocalBotId(result.bots[0].id);
        }
      } else {
        setBots([]);
        setBotsError(result.error || "Fehler beim Laden der Bots");
      }
    } catch (err) {
      console.error("[Blockbrain] handleLoadBots failed:", err);
      setBots([]);
      setBotsError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingBots(false);
    }
  };

  return {
    apiKey: localApiKey,
    apiUrl: localApiUrl,
    botId: localBotId,
    isLoading: isLoading || isSaving,
    isTesting,
    testResult,
    showApiKey,
    hasChanges,
    bots,
    isLoadingBots,
    botsError,
    handleApiKeyChange,
    handleApiUrlChange,
    handleBotIdChange,
    toggleApiKeyVisibility,
    handleSaveCredentials,
    handleTestConnection,
    handleLoadBots,
    setLocalBotId,
  };
}
